import type { Core, Course, Day, Kind, Schedule, Session, Slot, Term, Unit } from "./types";
import { toMin } from "./text";
import { weeksOverlap } from "./weeks";
import { travelMinutes } from "./travel";

export const DAYS: Day[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const KINDS: Kind[] = ["P", "AV", "LV", "S", "T", "other"];
export const PALETTE = ["#2438e8", "#9c1f33", "#17684a", "#c26a00", "#6b2fb3", "#00838f", "#b0306a", "#5a6b00", "#8a4b2a", "#3d5a80", "#d1495b", "#2a9d8f"];

export interface Index {
  core: Core;
  courses: Map<string, Course>;
  sessionsByCourse: Map<string, Session[]>;
  sessionsByKey: Map<string, Session>;
  units: Map<string, Unit>;
}

export function buildIndex(core: Core): Index {
  const courses = new Map(core.courses.map((c) => [c.id, c]));
  const sessionsByCourse = new Map<string, Session[]>();
  const sessionsByKey = new Map<string, Session>();
  for (const s of core.sessions) {
    if (!courses.has(s.course_id) || toMin(s.start) == null || toMin(s.end) == null) continue;
    const c = courses.get(s.course_id)!;
    s.term = s.term || c.term || null;
    (sessionsByCourse.get(s.course_id) ?? sessionsByCourse.set(s.course_id, []).get(s.course_id)!).push(s);
    sessionsByKey.set(s.key, s);
  }
  for (const c of core.courses) if (!c.term && sessionsByCourse.get(c.id)?.length) c.term = sessionsByCourse.get(c.id)![0].term;
  return { core, courses, sessionsByCourse, sessionsByKey, units: new Map(core.units.map((u) => [u.code, u])) };
}

/** Group label: lectures group by program/stream; other kinds by student group (prefixed by program when a course spans several). */
export function groupKey(s: Session, all: Session[]): string {
  if (s.kind === "P") return s.program || s.group || "-";
  const progs = new Set(all.map((x) => x.program || ""));
  return [progs.size > 1 ? s.program : null, s.group].filter(Boolean).join(" · ") || "-";
}

export type KindGroups = Partial<Record<Kind, Record<string, Session[]>>>;

export function kindGroups(idx: Index, courseId: string, term?: Term): KindGroups {
  const all = idx.sessionsByCourse.get(courseId) ?? [];
  const out: KindGroups = {};
  for (const s of all) {
    if (term && s.term && s.term !== term) continue;
    const k = (KINDS.includes(s.kind) ? s.kind : "other") as Kind;
    ((out[k] ??= {})[groupKey(s, all)] ??= []).push(s);
  }
  return out;
}

export const defaultSel = (groups: Record<string, Session[]>) => (Object.keys(groups).length > 1 ? "auto" : "all");

const slotOverlap = (a: Slot, b: Slot) =>
  a.day === b.day && weeksOverlap(a.weeks, b.weeks) ? Math.max(0, Math.min(a.end, b.end) - Math.max(a.start, b.start)) : 0;

export function transferGap(a: Slot, b: Slot, units: Map<string, Unit>): { gap: number; need: number } | null {
  if (a.day !== b.day || !a.fac || !b.fac || a.fac === b.fac) return null;
  const need = travelMinutes(units.get(a.fac), units.get(b.fac));
  if (!need) return null;
  const gap = a.end <= b.start ? b.start - a.end : b.end <= a.start ? a.start - b.end : null;
  return gap != null && gap < need ? { gap, need } : null;
}

export interface Resolved { slots: Slot[]; auto: { id: string; kind: Kind; group: string }[]; warnings: string[] }

function sessionSlot(idx: Index, s: Session, color: string, sched: Schedule): Slot | null {
  const c = idx.courses.get(s.course_id)!;
  const e = sched.edits[s.key];
  if (e?.hidden) return null;
  const stale = !!e?.base && (e.base.start !== s.start || e.base.end !== s.end || e.base.day !== s.day || (e.base.room ?? null) !== (s.room ?? null));
  const pick = sched.picks.find((p) => p.id === c.id);
  return {
    id: s.key, courseId: c.id, title: pick?.label || c.name, kind: s.kind, day: (e?.day ?? s.day) as Day,
    start: toMin(e?.start ?? s.start)!, end: toMin(e?.end ?? s.end)!, room: e?.room ?? s.room, lecturer: s.lecturer,
    group: s.group, weeks: s.weeks, fac: c.fac, color, academicYear: s.academic_year, edited: !!e && !e.hidden, stale, note: e?.note,
  };
}

/** Resolve a schedule into concrete weekly slots, auto-picking groups to minimise clashes and transfers. */
export function resolve(idx: Index, sched: Schedule, term: Term = sched.term): Resolved {
  const fixed: Slot[] = [], autos: { id: string; kind: Kind; groups: Record<string, Session[]>; color: string }[] = [];
  const warnings: string[] = [];
  sched.picks.forEach((p, i) => {
    const c = idx.courses.get(p.id);
    if (!c) { warnings.push(`Course ${p.id} is no longer in the catalogue.`); return; }
    const color = p.color || PALETTE[i % PALETTE.length];
    for (const [k, groups] of Object.entries(kindGroups(idx, p.id, term)) as [Kind, Record<string, Session[]>][]) {
      let sel = p.sel[k] ?? defaultSel(groups);
      if (sel !== "all" && sel !== "auto" && sel !== "off" && !groups[sel]) {
        warnings.push(`${c.name}: grupa „${sel}“ više ne postoji u rasporedu, pa je grupa izabrana automatski.`);
        sel = Object.keys(groups).length > 1 ? "auto" : "all";
      }
      if (sel === "off") continue;
      if (sel === "auto") { autos.push({ id: p.id, kind: k, groups, color }); continue; }
      const chosen = sel === "all" ? Object.values(groups).flat() : groups[sel];
      for (const s of chosen) { const sl = sessionSlot(idx, s, color, sched); if (sl) fixed.push(sl); }
    }
  });
  for (const ev of sched.custom.filter((e) => e.term === term)) {
    const a = toMin(ev.start), b = toMin(ev.end);
    if (a == null || b == null) continue;
    fixed.push({ id: `custom:${ev.id}`, title: ev.title, kind: "custom", day: ev.day, start: a, end: b, room: ev.place, color: ev.color || "#555", edited: true });
  }
  const auto: Resolved["auto"] = [];
  for (const a of autos) {
    let best = "", bestCost = Infinity;
    for (const [g, set] of Object.entries(a.groups)) {
      const slots = set.map((s) => sessionSlot(idx, s, a.color, sched)).filter(Boolean) as Slot[];
      let cost = 0;
      for (const s of slots) for (const f of fixed) { cost += slotOverlap(s, f) * 10; if (transferGap(s, f, idx.units)) cost += 15; }
      if (cost < bestCost) { best = g; bestCost = cost; }
    }
    for (const s of a.groups[best]) { const sl = sessionSlot(idx, s, a.color, sched); if (sl) fixed.push(sl); }
    auto.push({ id: a.id, kind: a.kind, group: best });
  }
  return { slots: fixed, auto, warnings };
}

export interface Analysis { clashes: [Slot, Slot][]; transfers: [Slot, Slot, { gap: number; need: number }][]; clashIds: Set<string> }

export function analyse(slots: Slot[], units: Map<string, Unit>): Analysis {
  const clashes: Analysis["clashes"] = [], transfers: Analysis["transfers"] = [], clashIds = new Set<string>();
  for (let i = 0; i < slots.length; i++) for (let j = i + 1; j < slots.length; j++) {
    const a = slots[i], b = slots[j];
    if (slotOverlap(a, b) > 0) { clashes.push([a, b]); clashIds.add(a.id); clashIds.add(b.id); }
    const t = transferGap(a, b, units);
    if (t) transfers.push([a, b, t]);
  }
  return { clashes, transfers, clashIds };
}

/** True if, for every kind, at least one group fits around the other slots. */
export function fitsWeek(idx: Index, courseId: string, term: Term, slots: Slot[]): boolean {
  const kg = kindGroups(idx, courseId, term);
  const kinds = Object.values(kg);
  if (!kinds.length) return false;
  const others = slots.filter((s) => s.courseId !== courseId);
  const dummy: Schedule = { name: "", term, picks: [], edits: {}, custom: [] };
  return kinds.every((groups) => Object.values(groups!).some((set) =>
    set.every((s) => { const sl = sessionSlot(idx, s, "", dummy)!; return others.every((o) => !slotOverlap(sl, o)); })));
}

export function laneLayout(slots: Slot[]): Map<string, { lane: number; lanes: number }> {
  const out = new Map<string, { lane: number; lanes: number }>();
  for (const d of DAYS) {
    const ds = slots.filter((s) => s.day === d).sort((a, b) => a.start - b.start || b.end - a.end);
    let cluster: Slot[] = [], end = -1;
    const lanes: number[] = [];
    const flush = () => { const n = Math.max(...cluster.map((s) => out.get(s.id)!.lane)) + 1; cluster.forEach((s) => (out.get(s.id)!.lanes = n)); cluster = []; lanes.length = 0; };
    for (const s of ds) {
      if (s.start >= end && cluster.length) flush();
      let li = lanes.findIndex((e) => e <= s.start);
      if (li < 0) { li = lanes.length; lanes.push(0); }
      lanes[li] = s.end;
      out.set(s.id, { lane: li, lanes: 1 });
      cluster.push(s);
      end = Math.max(end, s.end);
    }
    if (cluster.length) flush();
  }
  return out;
}
